import type { ProductCode } from '@data/config/reports';
import type { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import type { MetricV2Type } from '@data/hooks/api/use-metrics-v2-api/use-metrics-v2-api';

export interface MetricRule {
  metric: MetricV2Type;
  /** Which product codes this metric applies to. Omit = all products. */
  products?: ProductCode[];
  /** Required drilldown level. Omit = all drilldowns. */
  drilldown?: Drilldown;
  /**
   * Product codes that, when they are the ONLY codes the user holds for this
   * app, cause this metric to be excluded.
   *
   * Example: `excludedByProductCodes: ['R4RWB']` means the metric is hidden
   * when the user's R4R products consist solely of R4RWB.
   */
  excludedByProductCodes?: string[];
}

/**
 * Resolves a list of metric rules into concrete MetricV2Type[] based on
 * the current product and drilldown context.
 *
 * Rules without `products` apply to all products.
 * Rules without `drilldown` apply to all drilldown levels.
 *
 * @param userProductCodes - Optional array of the user's product codes for the
 *   current application. Used to evaluate `excludedByProductCodes` on each rule.
 */
export function resolveMetrics(
  rules: MetricRule[],
  productId: ProductCode,
  drilldown: Drilldown,
  userProductCodes?: string[]
): MetricV2Type[] {
  return rules
    .filter((rule) => {
      // Filter by app-level product
      if (rule.products && !rule.products.includes(productId)) return false;
      // Filter by drilldown level
      if (rule.drilldown && rule.drilldown !== drilldown) return false;
      // Filter by product-code-level exclusion
      if (
        rule.excludedByProductCodes &&
        userProductCodes &&
        userProductCodes.length > 0
      ) {
        const hasOnlyExcludedProducts = userProductCodes.every((code) =>
          rule.excludedByProductCodes!.includes(code)
        );
        if (hasOnlyExcludedProducts) return false;
      }
      return true;
    })
    .map((rule) => rule.metric);
}
